import React, { ReactNode, useEffect, useState, useCallback } from "react";
import { usePageMetadata } from "../context/PageMetadataContext";
import { Table, Spinner, Badge, Row, Col, Pagination } from "react-bootstrap";
import { Card, CardContent } from "../components/Card";
import { Link } from "react-router-dom";
import { 
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, 
  ResponsiveContainer, Cell, PieChart, Pie, Legend
} from "recharts";
import { PiscanAPI } from "../api/piscan";
import AccountLabel from "../components/AccountLabel";
// import AdUnit from "../components/AdUnit";
import { useLanguage } from "../context/LanguageContext"; // Add this import
import AdSenseUnit from "../components/AdsenseUnit";
import AdUnit from "../components/AdUnit";

interface TopAccount {
  account_id: string;
  balance: number;
}

interface TopAccountsResponse {
  accounts: TopAccount[];
  pagination: {
    limit: number;
    page: number;
    total_items: number;
    total_pages: number;
  };
}

interface DistributionRange {
  rangeDescription: ReactNode;
  displayName: ReactNode;
  percentage: ReactNode;
  min_balance: number;
  max_balance: number;
  count: number;
}

interface DistributionResponse {
  distribution: DistributionRange[];
  total_ranges: number;
}

const AccountStats: React.FC = () => {
  const { language, t } = useLanguage(); // Add language context hook
  const [topAccounts, setTopAccounts] = useState<TopAccount[]>([]);
  const [accountDistribution, setAccountDistribution] = useState<DistributionRange[]>([]);
  const [loading, setLoading] = useState<{ topAccounts: boolean; distribution: boolean }>({ topAccounts: true, distribution: true });
  const [error, setError] = useState<string | null>(null);
  const { setHeading, setTitle, setDescription } = usePageMetadata();
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [totalPages, setTotalPages] = useState<number>(1);
  const [totalAccounts, setTotalAccounts] = useState<number>(0);
  const [piPrice, setPiPrice] = useState<number>(2.0); // Default value before loading
  const [translationsReady, setTranslationsReady] = useState<boolean>(false);

  // Move the getAnimalCategory function inside a useCallback to ensure it's only created once translations are ready
  const getAnimalCategory = useCallback((min: number): string => {
    const minPi = min / 10_000_000;
    
    try {
      if (minPi >= 10_000_000) {
        return t('account_stats.categories.whales') || 'Whales';
      } else if (minPi >= 1_000_000) {
        return t('account_stats.categories.sharks') || 'Sharks'; 
      } else if (minPi >= 100_000) {
        return t('account_stats.categories.dolphins') || 'Dolphins';
      } else if (minPi >= 10_000) {
        return t('account_stats.categories.tunas') || 'Tunas';
      } else if (minPi >= 1_000) {
        return t('account_stats.categories.fish') || 'Fish';
      } else if (minPi >= 100) {
        return t('account_stats.categories.shrimps') || 'Shrimps';
      } else if (minPi >= 10) {
        return t('account_stats.categories.plankton') || 'Plankton';
      } else {
        return t('account_stats.categories.microbes') || 'Microbes';
      }
    } catch (err) {
      console.error("Translation error in getAnimalCategory:", err);
      // Fallback to English if translations fail
      const categories = {
        10_000_000: 'Whales',
        1_000_000: 'Sharks',
        100_000: 'Dolphins',
        10_000: 'Tunas',
        1_000: 'Fish',
        100: 'Shrimps',
        10: 'Plankton',
        0: 'Microbes'
      };
      
      for (const [threshold, name] of Object.entries(categories)) {
        if (minPi >= Number(threshold)) return name;
      }
      return 'Microbes';
    }
  }, [t]); // Depend on the translation function

  useEffect(() => {
    // Check if translations are ready (t is a function and returns values)
    try {
      if (typeof t === 'function' && t('account_stats.heading')) {
        setTranslationsReady(true);
      }
    } catch (err) {
      console.error("Translation check failed:", err);
    }
  }, [t]);

  useEffect(() => {
    // Only set metadata when translations are ready
    if (translationsReady) {
      setHeading(t('account_stats.heading'));
      setTitle(t('account_stats.title'));
      setDescription(t('account_stats.description'));
    }
  }, [translationsReady, setHeading, setTitle, setDescription, t]);

  useEffect(() => {
    fetchTopAccounts(currentPage);
  }, [currentPage]);

  useEffect(() => {
    // Only fetch distribution when translations are ready
    if (translationsReady) {
      fetchDistribution();
    }
  }, [translationsReady, getAnimalCategory]);

  useEffect(() => {
    fetchPiPrice();
  }, []);

  const fetchTopAccounts = async (page: number) => {
    try {
      setLoading(prevState => ({ ...prevState, topAccounts: true }));
      const topAccountsRes = await fetch(`${PiscanAPI.TopAccount}?page=${page}`).catch(() => ({ ok: false }));
      
      if (topAccountsRes.ok && 'json' in topAccountsRes) {
        const topAccountsData: TopAccountsResponse = await topAccountsRes.json();
        setTopAccounts(topAccountsData.accounts);
        setTotalPages(topAccountsData.pagination.total_pages);
        setTotalAccounts(topAccountsData.pagination.total_items);
      } else {
        setTopAccounts([]);
        console.error("Failed to fetch top accounts data");
      }
    } catch (err) {
      console.error("Error fetching top accounts:", err);
    } finally {
      setLoading(prevState => ({ ...prevState, topAccounts: false }));
    }
};

const fetchDistribution = async () => {
    if (!translationsReady) {
      console.log("Skipping distribution fetch - translations not ready");
      return;
    }
    
    try {
      setLoading(prevState => ({ ...prevState, distribution: true }));
      const distributionRes = await fetch(PiscanAPI.AccountsDistribution).catch(() => ({ ok: false }));
      
      if (distributionRes.ok && 'json' in distributionRes) {
        const distributionData: DistributionResponse = await distributionRes.json();
        
        

        // Process the data to make it more readable
        const processedData = distributionData.distribution.map(range => {
          // Format labels for readability
          const minFormatted = formatLargeNumber(range.min_balance);
          const maxFormatted = range.max_balance === -10000000 
            ? "π+" 
            : formatLargeNumber(range.max_balance);

          // Add animal category
          const animalCategory = getAnimalCategory(range.min_balance);
          
          return {
            ...range,
            // Add a displayName for the chart
            displayName: `${animalCategory}`,
            // Add range description
            rangeDescription: `${minFormatted} ${maxFormatted=='π+'?'':'-'} ${maxFormatted} ${maxFormatted=='π+'?'':'π'}`,
            // Add percentage for chart
            percentage: 0, // Will calculate after we have all data
            animalCategory
          };
        });
        
        // Calculate percentages after we have all data
        const totalAccounts = processedData.reduce((sum, item) => sum + item.count, 0);
        const dataWithPercentages = processedData.map(item => ({
          ...item,
          percentage: ((item.count / totalAccounts) * 100).toFixed(4)
        })).sort((a, b) => b.min_balance - a.min_balance); // Sort by count in descending order
        
        setAccountDistribution(dataWithPercentages);
      } else {
        setAccountDistribution([]);
        console.error("Failed to fetch account distribution data");
      }
    } catch (err) {
      console.error("Error fetching distribution data:", err);
      // Set fallback data or empty array
      setAccountDistribution([]);
    } finally {
      setLoading(prevState => ({ ...prevState, distribution: false }));
    }
  };

  const fetchPiPrice = async () => {
    try {
      // Fetch from OKX API
      const response = await fetch('https://api.piscan.io/data/pi-price');
      if (response.ok) {
        const data = await response.json();
        if (data && data.data && data.data[0]) {
          setPiPrice(parseFloat(data.data[0].idxPx));
        }
      }
    } catch (error) {
      console.error("Failed to fetch Pi price:", error);
      // Keep using default price if fetch fails
    }
  };

  // Format Pi value with language support
  const formatPi = (strathValue: number): string => {
    return (strathValue / 10_000_000).toLocaleString(language, { 
      minimumFractionDigits: 0, 
      maximumFractionDigits: 0 
    });
  };
  
  // Format large numbers with abbreviations (K, M, B)
  const formatLargeNumber = (value: number): string => {
    const piValue = value / 10_000_000;
    
    // Use translation keys for abbreviations
    if (piValue >= 1_000_000_000) {
      return (piValue / 1_000_000_000).toFixed(1) + t('number.billion_short');
    } else if (piValue >= 1_000_000) {
      return (piValue / 1_000_000).toFixed(1) + t('number.million_short');
    } else if (piValue >= 1_000) {
      return (piValue / 1_000).toFixed(1) + t('number.thousand_short');
    } else {
      return piValue.toFixed(2);
    }
  };
  
  // Truncate account address for display
  const truncateAddress = (address: string): string => {
    return `${address.substring(0, 6)}...${address.substring(address.length - 6)}`;
  };
  
  // Handle page changes
  const handlePageChange = (pageNumber: number) => {
    setCurrentPage(pageNumber);
  };
  
  // Custom tooltip for the bar chart
  const CustomBarTooltip = ({ active, payload }: any) => {
    if (active && payload && payload.length) {
      return (
        <div className="custom-tooltip bg-white p-2 shadow-sm border">
          <p className="mb-1 fw-bold">{payload[0].payload.displayName}</p>
          <p className="text-muted small mb-2">{payload[0].payload.rangeDescription}</p>
          <p className="mb-0">
            <span className="text-primary">{payload[0].payload.count.toLocaleString(language)}</span> {t('account_stats.accounts')}
          </p>
          <p className="mb-0 text-muted">
            {payload[0].payload.percentage}% {t('account_stats.of_total')}
          </p>
        </div>
      );
    }
    return null;
  };

  // Format USD values with language support
  const formatUSD = (piAmount: number): string => {
    const usdValue = (piAmount / 10_000_000) * piPrice;
    return usdValue.toLocaleString(language, { 
      minimumFractionDigits: 0, 
      maximumFractionDigits: 0 
    });
  };

  // Colors for pie chart sections
  const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884d8', '#FF6B6B', '#6BCB77', '#4D96FF'];

  // Pagination component with translations
  const renderPagination = () => {
    const items = [];
    const maxPagesToShow = 5;
    
    // Always show first page
    items.push(
      <Pagination.Item key={1} active={currentPage === 1} onClick={() => handlePageChange(1)}>
        1
      </Pagination.Item>
    );
    
    // Calculate start and end page numbers
    let startPage = Math.max(2, currentPage - Math.floor(maxPagesToShow / 2));
    let endPage = Math.min(totalPages - 1, startPage + maxPagesToShow - 2);
    
    // Adjust if we're near the beginning
    if (startPage > 2) {
      items.push(<Pagination.Ellipsis key="ellipsis-start" />);
    }
    
    // Add middle pages
    for (let page = startPage; page <= endPage; page++) {
      items.push(
        <Pagination.Item key={page} active={currentPage === page} onClick={() => handlePageChange(page)}>
          {page}
        </Pagination.Item>
      );
    }
    
    // Adjust if we're near the end
    if (endPage < totalPages - 1) {
      items.push(<Pagination.Ellipsis key="ellipsis-end" />);
    }
    
    // Always show last page if there is more than one page
    if (totalPages > 1) {
      items.push(
        <Pagination.Item 
          key={totalPages} 
          active={currentPage === totalPages} 
          onClick={() => handlePageChange(totalPages)}
        >
          {totalPages}
        </Pagination.Item>
      );
    }
    
    return (
      <Pagination className="justify-content-center mt-4">
        <Pagination.Prev 
          onClick={() => handlePageChange(Math.max(1, currentPage - 1))}
          disabled={currentPage === 1}
        >
          {t('pagination.previous')}
        </Pagination.Prev>
        {items}
        <Pagination.Next 
          onClick={() => handlePageChange(Math.min(totalPages, currentPage + 1))}
          disabled={currentPage === totalPages}
        >
          {t('pagination.next')}
        </Pagination.Next>
      </Pagination>
    );
  };

  if (loading.topAccounts && loading.distribution && !topAccounts.length && !accountDistribution.length) {
    return (
      <div className="d-flex justify-content-center my-5">
        <Spinner animation="border" role="status">
          <span className="visually-hidden">{t('common.loading')}</span>
        </Spinner>
      </div>
    );
  }

  return (
    <div className="account-statistics-page">
      <div className="mb-4">
        <p className="text-muted">
          {t('account_stats.intro_text')}
        </p>
      </div>

      {/* Account Distribution Visualizations */}
      <Row className="g-4 mb-4">
        {/* Bar Chart */}
        <Col lg={6}>
          <Card className="none-radius">
            <CardContent>
              <h5 className="card-title">{t('account_stats.distribution_chart_title')}</h5>
              <ResponsiveContainer width="100%" height={450}>
                <BarChart
                  data={accountDistribution}
                  margin={{
                    top: 20,
                    right: 30,
                    left: 20,
                    bottom: 70
                  }}
                >
                  <CartesianGrid strokeDasharray="3 3" vertical={false} />
                  <XAxis 
                    dataKey="displayName" 
                    angle={-45} 
                    textAnchor="end"
                    height={10}
                    tick={{fontSize: 12}}
                  />
                  <YAxis 
                    scale="log" // Change to logarithmic scale
                    domain={['auto', 'auto']}
                    tickFormatter={(value) => {
                      if (value === 0) return '0';
                      // Format large numbers with abbreviations
                      if (value >= 1000000) return (value / 1000000).toFixed(1) + t('number.million_short');
                      if (value >= 1000) return (value / 1000).toFixed(1) + t('number.thousand_short');
                      return value;
                    }}
                    label={{ 
                      value: '', 
                      angle: -90, 
                      position: 'insideLeft',
                      style: { textAnchor: 'middle' }
                    }}
                  />
                  <Tooltip content={<CustomBarTooltip />} />
                  <Bar dataKey="count" fill="#0088FE">
                    {accountDistribution.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </Col>
        
        <div className="row g-3 mb-4 d-block d-md-none">
        {/* <AdSenseUnit/> */}
        {/* <AdUnit
                  className="67cca9efaa72d3d47fe14312"
                  width="300px"
                  height="100px"
                  centered={true}
                /> */}
      </div>
                    
        {/* Distribution Table */}
        <Col lg={6}>
          <Card className="none-radius h-100">
            <CardContent>
              <h5 className="card-title">{t('account_stats.categories_title')}</h5>
              <div className="table-responsive">
                <Table hover className="mt-3 mb-0">
                  <thead>
                    <tr>
                      <th>{t('account_stats.table.category')}</th>
                      <th>{t('account_stats.table.balance_range')}</th>
                      <th>{t('account_stats.table.usd_value')}</th>
                      <th className="text-end">{t('account_stats.table.accounts')}</th>
                      <th className="text-end">{t('account_stats.table.percentage')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {/* Map through distribution ranges */}
                    {accountDistribution.map((range, index) => (
                      <tr key={index}>
                        <td>
                          <span className="fw-bold" style={{ color: COLORS[index % COLORS.length] }}>
                            {range.displayName}
                          </span>
                        </td>
                        <td>{range.rangeDescription}</td>
                        <td>
                          ${formatUSD(range.min_balance)} 
                          {range.max_balance !== -10000000 ? ` - $${formatUSD(range.max_balance)}` : '+'}
                        </td>
                        <td className="text-end">{range.count.toLocaleString(language)}</td>
                        <td className="text-end">
                          <Badge bg="light" text="dark">
                            {range.percentage}%
                          </Badge>
                        </td>
                      </tr>
                    ))}
                    
                    {/* Total row */}
                    <tr className="fw-bold" style={{ backgroundColor: 'rgba(0,0,0,0.03)' }}>
                      <td colSpan={3}>{t('account_stats.table.total')}</td>
                      <td className="text-end">{totalAccounts.toLocaleString(language)}</td>
                      <td className="text-end">
                        <Badge bg="primary" text="light">
                          100%
                        </Badge>
                      </td>
                    </tr>
                  </tbody>
                </Table>
              </div>
            </CardContent>
          </Card>
        </Col>
      </Row>

      {/* Top Accounts Table */}
      <Card className="none-radius">
        <CardContent>
          <h5 className="card-title">{t('account_stats.top_accounts_title')}</h5>
          <div className="table-responsive mt-3">
            <Table hover className="mb-0">
              <thead>
                <tr>
                  <th>{t('account_stats.table.rank')}</th>
                  <th>{t('account_stats.table.account')}</th>
                  <th className="text-end">{t('account_stats.table.balance')} (π)</th>
                  <th className="text-end">{t('account_stats.table.value')} (USD)</th>
                  <th className="text-end">{t('account_stats.table.supply_percentage')}</th>
                </tr>
              </thead>
              <tbody>
                {topAccounts.map((account, index) => (
                  <tr key={account.account_id}>
                    <td>
                      <Badge 
                        bg={index < 3 ? "warning" : "light"} 
                        text={index < 3 ? "dark" : "dark"}
                      >
                        #{(currentPage - 1) * 20 + index + 1}
                      </Badge>
                    </td>
                    <td>
                      <AccountLabel account={account.account_id} shorten={true} />
                    </td>
                    <td className="text-end fw-bold">
                      {formatPi(account.balance)}
                    </td>
                    <td className="text-end">
                      ${formatUSD(account.balance)}
                    </td>
                    <td className="text-end">
                      <Badge bg="info" text="dark">
                        {(account.balance / 10845382064.7946 / 10_000_000 * 100).toFixed(4)}%
                      </Badge>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </div>
          {renderPagination()}
        </CardContent>
      </Card>
    </div>
  );
};

export default AccountStats;